import {
  ButtonType,
  CaesarsButton,
  useIsDesktop,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {Icon, Text, View} from 'native-base';
import {IViewProps} from 'native-base/lib/typescript/components/basic/View/types';
import React, {ReactNode, useEffect, useRef} from 'react';
import {Animated} from 'react-native';
import {Platform, UIManager} from 'react-native';
import FontAwesome5Pro from 'react-native-vector-icons/FontAwesome5Pro';
import {stripHtmlCode} from 'utils';

import {Offer, OfferSource, RewardState} from 'models/Offer';

import OfferDetailsButtonSpacing from './OfferDetailsButtonSpacing';

interface OfferDetailsStatusProps extends IViewProps {
  offer?: Offer;
  isAccepted?: boolean;
  postAcceptMessage?: string | undefined;
  isDownloadVisible?: boolean;
  setIsDownloadVisible?: (isVisible: boolean) => void;
  onDownloadPress?: () => void;
}

const OfferDetailsStatus: React.FC<OfferDetailsStatusProps> = ({
  offer,
  isAccepted = false,
  postAcceptMessage = undefined,
  isDownloadVisible = false,
  setIsDownloadVisible = () => {},
  onDownloadPress = () => {},
  ...viewProps
}) => {
  const isDesktop = useIsDesktop();
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const viewRef = useRef();
  const OCRMOfferIsAccepted =
    offer?.source === OfferSource.ocrm &&
    offer.ocrm.rewards[0].state === RewardState.SUCCESS;

  useEffect(() => {
    if (viewRef.current && Platform.OS === 'web' && OCRMOfferIsAccepted) {
      UIManager?.focus(viewRef.current);
    }
  }, [OCRMOfferIsAccepted]);

  useEffect(() => {
    if (OCRMOfferIsAccepted && postAcceptMessage) {
      fadeAnim.setValue(0);

      Animated.timing(fadeAnim, {
        duration: 150,
        toValue: 1,
        useNativeDriver: false,
      }).start();
    }
  }, [fadeAnim, OCRMOfferIsAccepted, postAcceptMessage]);

  const renderStatus = ({
    offerStatus,
    message,
  }: {
    offerStatus: string;
    message?: string | ReactNode;
  }) => (
    <View
      ref={viewRef}
      accessibilityLabel={offerStatus}
      focusable={true}
      width="100%"
      alignItems="center"
      backgroundColor="#F8F8FA"
      p={5}
      mb={6}
      {...viewProps}>
      <Icon
        as={FontAwesome5Pro}
        name={isDownloadVisible ? 'exclamation-circle' : 'check-circle'}
        // @ts-expect-error Native Base icon throws a type error for the "light" prop
        light={true}
        color={isDownloadVisible ? '#F57F17' : '#00C853'}
        size="64px"
        lineHeight="70px"
        mb={5}
      />
      <Text
        color="tuna"
        fontSize={isDesktop ? '24px' : '18px'}
        fontFamily="heading"
        fontWeight="extrabold"
        lineHeight="28px"
        mb={4}>
        {offerStatus}
      </Text>
      {message ? (
        <Text mb={5} textAlign="center">
          {typeof message === 'string' ? stripHtmlCode(message) : message}
        </Text>
      ) : null}
      {isDownloadVisible && (
        <View
          width="100%"
          flexDirection={isDesktop ? 'row' : 'column-reverse'}
          alignItems="center"
          justifyContent="center">
          <CaesarsButton
            type={ButtonType.TEXT_CTA}
            title="Cancel"
            isFullWidth={true}
            onPress={() => setIsDownloadVisible(false)}
          />
          <OfferDetailsButtonSpacing />
          <CaesarsButton
            title="Yes, Download"
            isFullWidth={true}
            onPress={() => {
              onDownloadPress();
              setIsDownloadVisible(false);
            }}
          />
        </View>
      )}
    </View>
  );

  if (isAccepted) {
    return renderStatus({
      offerStatus: 'OFFER ACCEPTED',
      message: postAcceptMessage,
    });
  }

  if (OCRMOfferIsAccepted) {
    return (
      <Animated.View
        style={{
          opacity: fadeAnim,
        }}>
        {renderStatus({
          offerStatus: 'OFFER ACCEPTED',
          message: postAcceptMessage,
        })}
      </Animated.View>
    );
  }

  if (offer?.source === OfferSource.pdb) {
    if (offer.status.toLowerCase() === 'reserved') {
      return renderStatus({offerStatus: 'OFFER RESERVED'});
    }
    if (offer.status.toLowerCase() === 'redeemed') {
      return renderStatus({offerStatus: 'OFFER REDEEMED'});
    }
  }

  if (isDownloadVisible) {
    return renderStatus({
      offerStatus: 'DOWNLOAD TO CARD?',
      message: (
        <Text>
          This offer
          <Text fontWeight="bold"> must be used within 72 hours. </Text>
          You won&apos;t be able to revert this.
        </Text>
      ),
    });
  }

  return null;
};

export default OfferDetailsStatus;
